/**
 * El horario propio de una campaña en el paso Confirmar (pedido de Llama Leads, 19/10/2026): su franja y sus días
 * (siempre dentro de la franja de la marca), cuántos salen como mucho por día y el último día. Todo opcional: sin
 * nada cargado la campaña sale con el horario de la marca y el cupo de Meta, como siempre. Pura.
 *
 * La zona (Alan, 26/10/2026): la franja propia, el último día y la hora programada se leen en la de la campaña; sin
 * elegir, la de la marca (Argentina). La de la marca no se manda: es la de siempre.
 */

import type { FranjaHoraria, HorarioCampana } from "../types";
import { validarFranjasLocal } from "./config";
import { num } from "../../campana-nueva/utils/format";
import { ZONA_MARCA } from "./zonas";

export interface HorarioEnEdicion {
  /** si eligió una franja propia (si no, la de la marca) */
  conFranja: boolean;
  franjas: FranjaHoraria[];
  /** lo que escribió en «Máximo por día» ("" = sin tope) */
  tope: string;
  /** «Último día» en AAAA-MM-DD ("" = sin fecha de fin) */
  hasta: string;
  /** la zona en que se leen la franja propia, el último día y la hora programada (`logic/zonas`) */
  zona: string;
}

export const HORARIO_VACIO: HorarioEnEdicion = { conFranja: false, franjas: [{ desde: "10:00", hasta: "13:00", dias: [1, 2, 3, 4, 5] }], tope: "", hasta: "", zona: ZONA_MARCA };

const FECHA_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Lo que no se puede mandar todavía: franjas mal escritas, un tope que no es un entero de 1 en adelante (ni más que el cupo de Meta), una fecha que no es fecha. */
export function erroresHorario(h: HorarioEnEdicion, limite?: number | null): string[] {
  const errores: string[] = [];
  if (h.conFranja) errores.push(...validarFranjasLocal(h.franjas));
  const tope = h.tope.trim();
  if (tope) {
    const n = Number(tope);
    if (!Number.isInteger(n) || n < 1) errores.push("El máximo por día tiene que ser un número entero, de 1 en adelante.");
    else if (typeof limite === "number" && limite > 0 && n > limite) errores.push(`Meta deja escribirle a ${num(limite)} personas por día: el máximo no puede ser más.`);
  }
  if (h.hasta && !FECHA_RE.test(h.hasta)) errores.push("El último día no es una fecha.");
  return errores;
}

/** El horario guardado en la campaña, para editarlo en el paso «Horario y baja» (sin horario: el de la marca, sin tope ni fin). */
export function horarioEnEdicion(h: HorarioCampana | null | undefined): HorarioEnEdicion {
  return {
    conFranja: !!h?.franjas?.length,
    franjas: h?.franjas?.length ? h.franjas : HORARIO_VACIO.franjas,
    tope: typeof h?.topeDiario === "number" ? String(h.topeDiario) : "",
    hasta: h?.hasta || "",
    zona: h?.zona || ZONA_MARCA,
  };
}

/**
 * ¿Es el mismo horario? Lo que cambia el envío: la franja propia sólo si está elegida, el tope sin espacios, el último día
 * y la zona. Sirve para saber si lo de «Cuándo» ya está guardado.
 */
export function mismoHorario(a: HorarioEnEdicion, b: HorarioEnEdicion): boolean {
  const clave = (h: HorarioEnEdicion) => JSON.stringify([h.conFranja ? h.franjas : null, h.tope.trim(), h.hasta, h.zona || ZONA_MARCA]);
  return clave(a) === clave(b);
}

/** Lo que va al chequeo y al confirmar: null si no cargó nada (o si algo está mal: no se pide un chequeo roto). */
export function horarioParaEnviar(h: HorarioEnEdicion, limite?: number | null): HorarioCampana | null {
  if (erroresHorario(h, limite).length) return null;
  const franjas = h.conFranja ? h.franjas : null;
  const topeDiario = h.tope.trim() ? Number(h.tope) : null;
  const hasta = h.hasta || null;
  const zona = h.zona && h.zona !== ZONA_MARCA ? h.zona : null;
  return franjas || topeDiario !== null || hasta || zona ? { franjas, topeDiario, hasta, ...(zona ? { zona } : {}) } : null;
}

const DIAS = ["dom", "lun", "mar", "mié", "jue", "vie", "sáb"];

/** «09:00 a 21:00 (lun, mar, mié, jue, vie) · 09:00 a 13:00 (sáb)»: como `describirFranjas` del backend. */
export function textoFranjas(f: FranjaHoraria[] | null | undefined): string {
  return (f || []).map((x) => `${x.desde} a ${x.hasta} (${[...(x.dias || [])].sort((a, b) => ((a + 6) % 7) - ((b + 6) % 7)).map((d) => DIAS[d]).join(", ")})`).join(" · ");
}

/** «Sale 10:00 a 12:00 (lun, mar) · hasta 300 por día · hasta el 10/11» (la franja, la que manda el backend: ya es la intersección). */
export function textoHorario(h: HorarioCampana | null | undefined, franjasTexto?: string | null): string {
  if (!h) return "";
  const partes: string[] = [];
  if (h.franjas?.length && franjasTexto) partes.push(`Sale ${franjasTexto}`);
  if (typeof h.topeDiario === "number") partes.push(`hasta ${num(h.topeDiario)} por día`);
  if (h.hasta && FECHA_RE.test(h.hasta)) partes.push(`hasta el ${h.hasta.slice(8, 10)}/${h.hasta.slice(5, 7)}`);
  const texto = partes.join(" · ");
  return texto ? texto.charAt(0).toUpperCase() + texto.slice(1) : "";
}
